import Colors from "@microdoing/shared/constants/Colors";
import React, { useCallback, useEffect, useRef } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  Dimensions,
  RefreshControl,
} from "react-native";
import CoachingHeader from "./CoachingHeader";
import Fonts from "@microdoing/shared/constants/Fonts";
import { widthPercentageToDP } from "react-native-responsive-screen";
import CoachingTimeline from "./CoachingTimeline";
import CoachingGoals from "./CoachingGoals";
import { useDispatch, useSelector } from "react-redux";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { getActiveCoachingSessionRequest } from "@microdoing/shared/services/coaching/actions";
import {
  getCoachingRequesting,
  getIsCoachingFinished,
  getEndCoachingMessage,
  getIsForbidden,
} from "@microdoing/shared/services/coaching/selectors";
import FinishedCoaching from "./FinishedCoaching";
import EndCoachingMessage from "./EndCoachingMessage";
import SummaryCoaching from "./SummaryCoaching";
import SeparatorCoaching from "./SeparatorCoaching";
import { Box, CardButton, MainTitle } from "app/components/common";
import { logout } from "@microdoing/shared/services/login/actions";
import i18n from "@microdoing/shared/i18n";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import theme from "app/Theme/Theme";
import HeaderTabBar from "app/navigation/TabBar/HeaderTabBar";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const { width } = Dimensions.get("window");

const styles = StyleSheet.create({
  main: {
    backgroundColor: theme.colors["neutral.75"],
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  scrollView: {
    backgroundColor: theme.colors["neutral.75"],
    paddingBottom: 40,
    alignItems: "center",
  },
  scrollViewContainer: {
    width,
  },
  headerView: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    marginTop: 20,
    width: widthPercentageToDP("90%"),
  },
  header: {
    textAlign: "left",
    flex: 1,
    fontSize: 32,
    ...Fonts.montserrat.bold,
    color: theme.colors["neutral.900"],
  },
  button: {
    height: 55,
    width: widthPercentageToDP("90%"),
  },
});

const Coaching = ({ navigation }) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const isCoachingFinished = useSelector(getIsCoachingFinished);
  const endCoachingMessage = useSelector(getEndCoachingMessage);
  const isForbidden = useSelector(getIsForbidden);
  const scrollTop = useSelector(getScrollTop);

  const requesting = useSelector(getCoachingRequesting);
  const userId = useSelector(getUserId);

  const insets = useSafeAreaInsets();

  const handleDisconnect = () => {
    dispatch(logout({}));
  };

  const onRefresh = useCallback(() => {
    dispatch(getActiveCoachingSessionRequest({ userId }));
  }, [userId, dispatch]);

  useEffect(() => {
    onRefresh();
  }, [onRefresh]);

  useEffect(() => {
    if (scrollTop) {
      scrollToTop();
      dispatch(changeNavField({ scrollTop: false }));
    }
  }, [scrollTop, dispatch]);

  const scrollToTop = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  return (
    <Box
      style={[styles.main, { paddingTop: insets.top }]}
      testID="coaching-screen"
    >
      <ScrollView
        ref={scrollRef}
        refreshControl={
          <RefreshControl refreshing={requesting} onRefresh={onRefresh} />
        }
        style={styles.scrollViewContainer}
        contentContainerStyle={styles.scrollView}
      >
        <Box paddingHorizontal="sm" width="100%">
          <HeaderTabBar
            title={
              <MainTitle variant="l8-bold">{i18n.t("Coaching")}</MainTitle>
            }
          />
        </Box>
        <CoachingHeader />
        {isCoachingFinished && (
          <>
            <FinishedCoaching />
            {!!endCoachingMessage && (
              <EndCoachingMessage endCoachingMessage={endCoachingMessage} />
            )}
            <SummaryCoaching />
            {isForbidden && (
              <CardButton
                buttonPropStyle={styles.button}
                onPress={handleDisconnect}
                text={i18n.t("end-coaching")}
              />
            )}
            <SeparatorCoaching />
          </>
        )}
        <CoachingTimeline />
        <CoachingGoals />
      </ScrollView>
    </Box>
  );
};

export default Coaching;
